import {MutableRefObject} from "react";

// Twice the measured cold path (~5s MediaPipe init + PLAY_TIMEOUT_MS + three websocket round trips),
// and equal to WsClient's reconnect backoff cap, so one backoff step alone never abandons a link.
const LINK_TIMEOUT_MS = 30000

// Suspends the deadline around a call that waits on the USER: a first-time permission prompt has an
// OS dialog behind it and outlasts any sane timeout. ⚠ A caller must install what it captured with
// no await in between, or an abandoned link holds a capture nothing stops.
type LinkFence = { unclocked: <T>(work: () => Promise<T>) => Promise<T> }

// RISKS.md/P1.24: releases the chain after LINK_TIMEOUT_MS whether or not the link settled — one
// that never settles used to wedge it for the life of the page. The abandoned link keeps RUNNING,
// so whatever it resumes into has to re-check what it still owns.
export const enqueue = (
  chain: MutableRefObject<Promise<void>>,
  label: string,
  link: (fence: LinkFence) => Promise<void>,
  // Runs once when the chain is released, whether the link settled or was abandoned.
  onRelease?: () => void,
) => {
  chain.current = chain.current.then(() => new Promise<void>(resolve => {
    let timer: any = null
    let settled = false
    const settle = () => {
      if (settled) return
      settled = true
      if (timer) clearTimeout(timer)
      onRelease?.()
      resolve()
    }
    // Armed where the link STARTS, not where it is queued: one waiting behind a legitimately slow
    // link would otherwise be abandoned before its body ran.
    const arm = () => {
      timer = setTimeout(() => {
        timer = null
        // Nothing but the log belongs here: retrying, retracting or hiding a toast from this path
        // would act on state the link that replaced this one now owns.
        console.error(`${label} did not settle within ${LINK_TIMEOUT_MS} ms; releasing the chain`)
        settle()
      }, LINK_TIMEOUT_MS)
    }
    arm()
    const fence: LinkFence = {
      unclocked: async work => {
        if (timer) clearTimeout(timer)
        timer = null
        try {
          return await work()
        } finally {
          if (!settled) arm()
        }
      },
    }
    link(fence)
      .catch(e => console.warn(`${label} failed:`, e))
      .finally(settle)
  }))
}
